import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plug, Copy, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";
import { tierHasCapability } from "@/lib/tier";

type TokenRow = {
  id: string;
  workspace_id: string;
  name: string;
  token_prefix: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
};

type ActivityRow = {
  id: string;
  token_id: string;
  tool_name: string;
  result_status: string;
  error_code: string | null;
  duration_ms: number | null;
  created_at: string;
};

const MCP_URL = `https://ypyublryuaiiwemknnir.supabase.co/functions/v1/mcp-server`;

const QuickStartSnippets = ({ url }: { url: string }) => {
  const [open, setOpen] = useState(false);
  const claudeDesktop = JSON.stringify(
    {
      mcpServers: {
        excerptus: {
          url,
          headers: { Authorization: "Bearer excmcp_YOUR_TOKEN_HERE" },
        },
      },
    },
    null,
    2,
  );
  const cursor = JSON.stringify(
    {
      mcpServers: {
        excerptus: {
          url,
          headers: { Authorization: "Bearer excmcp_YOUR_TOKEN_HERE" },
        },
      },
    },
    null,
    2,
  );
  const copy = (label: string, text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="rounded-md border bg-muted/20 text-sm">
      <button
        type="button"
        className="w-full flex items-center justify-between p-3 text-left font-medium"
        onClick={() => setOpen((v) => !v)}
      >
        <span>Config snippets for token-based apps</span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="space-y-4 p-3 pt-0">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium">Claude Desktop (claude_desktop_config.json)</span>
              <Button size="sm" variant="ghost" onClick={() => copy("Claude Desktop config", claudeDesktop)}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
            </div>
            <pre className="text-xs bg-background border rounded p-2 overflow-auto">{claudeDesktop}</pre>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium">Cursor (~/.cursor/mcp.json)</span>
              <Button size="sm" variant="ghost" onClick={() => copy("Cursor config", cursor)}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
            </div>
            <pre className="text-xs bg-background border rounded p-2 overflow-auto">{cursor}</pre>
          </div>
          <p className="text-xs text-muted-foreground">
            Replace <code>excmcp_YOUR_TOKEN_HERE</code> with a token from the section below.
          </p>
        </div>
      )}
    </div>
  );
};

type GrantRow = {
  id: string;
  oauth_client_id: string;
  client_name: string | null;
  workspace_id: string;
  created_at: string;
  last_used_at: string | null;
};

/** One-click (OAuth) connections such as Claude.ai, with Disconnect. */
const ConnectedApps = ({
  workspaces,
  workspaceId,
}: {
  workspaces: { id: string; workspace_label?: string | null }[];
  workspaceId?: string;
}) => {
  const qc = useQueryClient();
  const { data: allGrants = [], isLoading } = useQuery({
    queryKey: ["mcp-oauth-grants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mcp_oauth_grants")
        .select("id, oauth_client_id, client_name, workspace_id, created_at, last_used_at")
        .is("revoked_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as GrantRow[];
    },
  });
  // Scope to this Persona's workspaces (or one workspace) so other Personas' connections never show here.
  const allowed = new Set(workspaces.map((w) => w.id));
  const grants = workspaceId
    ? allGrants.filter((g) => g.workspace_id === workspaceId)
    : allGrants.filter((g) => allowed.has(g.workspace_id));

  const disconnect = async (g: GrantRow) => {
    if (!confirm(`Disconnect ${g.client_name ?? "this app"}? It will lose access on its next request.`)) return;
    const { error } = await supabase
      .from("mcp_oauth_grants")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", g.id);
    if (error) {
      toast.error("Could not disconnect. Please try again.");
      return;
    }
    // Also cancel the approval at the sign-in server so the app must ask again.
    await supabase.auth.oauth.revokeGrant({ clientId: g.oauth_client_id }).catch(() => undefined);
    toast.success("Disconnected");
    qc.invalidateQueries({ queryKey: ["mcp-oauth-grants"] });
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">Connected apps</h4>
      {isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : grants.length === 0 ? (
        <p className="text-sm text-muted-foreground">No one-click connections yet.</p>
      ) : (
        <div className="rounded-md border divide-y">
          {grants.map((g) => {
            const ws = workspaces.find((w) => w.id === g.workspace_id);
            return (
              <div key={g.id} className="flex items-center justify-between p-3 gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{g.client_name ?? "Connected app"}</div>
                  <div className="text-xs text-muted-foreground">
                    {ws?.workspace_label?.trim() || "Workspace"} · connected {new Date(g.created_at).toLocaleDateString()} ·{" "}
                    {g.last_used_at ? `last used ${new Date(g.last_used_at).toLocaleString()}` : "never used"}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => disconnect(g)}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Disconnect
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Settings → Account: account-wide list of every approved one-click app. */
export const McpConnectedAppsCard = () => {
  const { workspaces } = useWorkspace();
  if (!workspaces.some((w) => tierHasCapability(w.account_tier, "mcp_inbound"))) return null;
  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="h-5 w-5 text-primary" /> Connected AI apps
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Every app you've approved for this account's workspaces. Each workspace's Claude address and personal
          tokens live in that workspace's tab → <strong>Manage APIs</strong>.
        </p>
        <ConnectedApps workspaces={workspaces} />
      </CardContent>
    </Card>
  );
};

/**
 * Workspace Settings → Manage APIs card: this workspace's Claude connector
 * address, its one-click connection, and its personal MCP tokens. Plus+.
 */
export const WorkspaceMcpCard = ({ workspace }: { workspace: Workspace }) => {
  const qc = useQueryClient();
  const eligible = tierHasCapability(workspace.account_tier, "mcp_inbound");
  const wsLabel = workspace.workspace_label?.trim() || workspace.display_name || "this workspace";
  const wsUrl = `${MCP_URL}/w/${workspace.id}`;
  const workspaceId = workspace.id;

  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [rawToken, setRawToken] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["mcp-tokens"],
    enabled: eligible,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<{
        tokens: TokenRow[];
        recent: ActivityRow[];
      }>("mcp-tokens-manage", { body: { action: "list" } });
      if (error) throw error;
      return data ?? { tokens: [], recent: [] };
    },
  });

  if (!eligible) {
    return (
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-primary" /> Connect Claude &amp; other AI apps
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Available on Plus and higher. Upgrade this workspace to connect it to Claude.
        </CardContent>
      </Card>
    );
  }

  const refresh = () => qc.invalidateQueries({ queryKey: ["mcp-tokens"] });

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Give the token a name");
      return;
    }
    setCreating(true);
    const { data: resp, error } = await supabase.functions.invoke<{
      raw_token: string;
      token: TokenRow;
    }>("mcp-tokens-manage", {
      body: { action: "create", workspace_id: workspaceId, name: name.trim() },
    });
    setCreating(false);
    if (error || !resp?.raw_token) {
      toast.error(error?.message ?? "Could not create token");
      return;
    }
    setRawToken(resp.raw_token);
    setRevealOpen(true);
    setName("");
    refresh();
  };

  const handleRevoke = async (id: string) => {
    if (!confirm("Revoke this token? Any MCP client using it will lose access immediately.")) return;
    const { error } = await supabase.functions.invoke("mcp-tokens-manage", {
      body: { action: "revoke", id },
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Token revoked");
    refresh();
  };

  const copyToken = () => {
    if (!rawToken) return;
    navigator.clipboard.writeText(rawToken);
    toast.success("Copied to clipboard");
  };

  const tokens = (data?.tokens ?? []).filter((t) => t.workspace_id === workspaceId);
  const tokenIds = new Set(tokens.map((t) => t.id));
  const recent = (data?.recent ?? []).filter((r) => tokenIds.has(r.token_id));
  const mcpWorkspaces = [workspace];

  return (
    <>
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-primary" /> Connect Claude &amp; other AI apps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="rounded-md border border-primary/30 bg-primary/5 p-4 text-sm space-y-3">
            <div className="flex items-center gap-2">
              <p className="font-medium">Claude.ai — one click</p>
              <Badge>Recommended</Badge>
            </div>
            <p className="text-muted-foreground text-xs">
              Requires a paid Claude plan (Pro, Team or Enterprise) — that's Anthropic's rule, not ours. Once
              connected, it works in every Claude app signed in to that account (web, desktop, mobile).
            </p>
            <p className="text-xs text-muted-foreground">
              This address connects Claude to <strong>{wsLabel}</strong> only. Each workspace has its own address
              in its own Manage APIs tab — add one connector per workspace in Claude to use several at once.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 block text-xs break-all rounded border bg-background p-2">{wsUrl}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(wsUrl); toast.success(`${wsLabel} connector URL copied`); }}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
            </div>
            <ol className="list-decimal pl-5 space-y-1 text-xs">
              <li>In Claude.ai open <strong>Settings → Connectors → Add custom connector</strong>.</li>
              <li>Name it <strong>Excerptus – {wsLabel}</strong> and paste the address above. Leave the advanced settings as detected.</li>
              <li>Click <strong>Add</strong>, sign in to Excerptus if asked, and click <strong>Approve</strong>.</li>
            </ol>
          </section>

          <ConnectedApps workspaces={[workspace]} workspaceId={workspace.id} />

          <section className="space-y-3">
            <div>
              <p className="text-sm font-medium">Free Claude Desktop, Cursor, Zed &amp; Cowork — personal tokens</p>
              <p className="text-xs text-muted-foreground">
                Manual setup: create a token below and paste it into your app's config file. Tokens here only work
                for {wsLabel}. Excerptus doesn't offer one-on-one help with manual setup — the one-click Claude.ai path
                above is the zero-friction option.
              </p>
            </div>
            <QuickStartSnippets url={wsUrl} />
          </section>



          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <div className="space-y-1">
              <Label htmlFor={`mcp-name-${workspace.id}`}>Token name (for {wsLabel})</Label>
              <Input
                id={`mcp-name-${workspace.id}`}
                placeholder="e.g. Cursor – laptop"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? "Creating…" : "Create token"}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Your tokens</h4>
              <Button variant="ghost" size="sm" onClick={refresh}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
            </div>
            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : tokens.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tokens yet.</p>
            ) : (
              <div className="rounded-md border divide-y">
                {tokens.map((t) => {
                  const ws = mcpWorkspaces.find((w) => w.id === t.workspace_id);
                  return (
                    <div key={t.id} className="flex items-center justify-between p-3 gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{t.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {ws?.workspace_label?.trim() ?? "Workspace"} ·{" "}
                          <code>excmcp_{t.token_prefix}_…</code> ·{" "}
                          {t.last_used_at ? `last used ${new Date(t.last_used_at).toLocaleString()}` : "never used"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {t.revoked_at ? (
                          <Badge variant="secondary">Revoked</Badge>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleRevoke(t.id)}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {recent.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Recent activity</h4>
              <div className="rounded-md border max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/30 text-left">
                    <tr>
                      <th className="p-2">When</th>
                      <th className="p-2">Tool</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="p-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                        <td className="p-2"><code>{r.tool_name}</code></td>
                        <td className="p-2">
                          {r.result_status === "ok" ? (
                            <span className="text-green-600">ok</span>
                          ) : (
                            <span className="text-destructive">{r.error_code ?? "error"}</span>
                          )}
                        </td>
                        <td className="p-2">{r.duration_ms ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={revealOpen} onOpenChange={setRevealOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this token now</DialogTitle>
            <DialogDescription>
              This is the only time you'll see this token. Copy it and paste it into your MCP
              client (e.g. Claude.ai). If you lose it, revoke and create a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 p-3 break-all font-mono text-sm">
            {rawToken}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={copyToken}>
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button onClick={() => setRevealOpen(false)}>I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
